import React, { useState, useEffect } from 'react';
import { useSeasonContext } from '../context/SeasonContext';
import { awardService } from '../services/awardService';
import '../pages/Pages.css';
import './AwardCandidates.css';

function AwardCandidates() {
  const [candidates, setCandidates] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { selectedSeason } = useSeasonContext();

  useEffect(() => {
    const loadCandidates = async () => {
      if (!selectedSeason) return;
      
      setLoading(true);
      setError(null);
      try {
        const data = await awardService.getAwardCandidates(selectedSeason.id);
        setCandidates(data);
      } catch (err) {
        setError('Failed to load award candidates.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadCandidates();
  }, [selectedSeason]);

  const AwardSection = ({ title, awardKey, candidates: awardCandidates, keyStats }) => {
    if (!awardCandidates || awardCandidates.length === 0) {
      return (
        <div className="award-section">
          <h3 className="award-title">{title}</h3>
          <p className="no-candidates">No eligible candidates</p>
        </div>
      );
    }

    const isBlocking = title.includes('OLOTY');

    return (
      <div className="award-section">
        <h3 className="award-title">{title}</h3>
        <div className="candidates-list">
          {awardCandidates.map((candidate, idx) => (
            <div key={idx} className="candidate-card">
              <div className="card-header">
                <div className="rank-badge">{idx + 1}</div>
                <div className="candidate-info">
                  {isBlocking ? (
                    <>
                      <h4 className="candidate-name">{candidate.TEAM || 'Unknown'}</h4>
                      <p className="candidate-detail">Team</p>
                    </>
                  ) : (
                    <>
                      <h4 className="candidate-name">{candidate.NAME || 'Unknown'}</h4>
                      <p className="candidate-detail">
                        {candidate.POS || 'N/A'} • {candidate.TEAM || 'Unknown'}
                      </p>
                    </>
                  )}
                </div>
                <div className="candidate-score">
                  <span className="score-label">Score</span>
                  <span className="score-value">{candidate.awardScore?.toFixed(3) || '0.000'}</span>
                </div>
              </div>
              {keyStats && keyStats.length > 0 && (
                <div className="candidate-stats">
                  {keyStats.map((statKey, i) => (
                    <div key={i} className="stat-item">
                      <span className="stat-label">{statKey}</span>
                      <span className="stat-value">{candidate[statKey] || '—'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading award candidates...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="error-message">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page award-candidates-page">
      <div className="page-header">
        <h1>Award Candidates</h1>
        <p>Top 5 contenders for every seasonal award</p>
      </div>

      <div className="awards-grid">
        <AwardSection
          title="🏈 QBOTY – Quarterback of the Year"
          awardKey="QBOTY"
          candidates={candidates.QBOTY}
          keyStats={['YRDS', 'TD', 'INT', 'CMP%', 'QBR']}
        />

        <AwardSection
          title="🏃 RBOTY – Running Back of the Year"
          awardKey="RBOTY"
          candidates={candidates.RBOTY}
          keyStats={['YRDS', 'TDS', 'ATT', 'AVG']}
        />

        <AwardSection
          title="📡 WROTY – Wide Receiver of the Year"
          awardKey="WROTY"
          candidates={candidates.WROTY}
          keyStats={['YRDS', 'TDS', 'REC', 'REC%']}
        />

        <AwardSection
          title="🎯 TEOTY – Tight End of the Year"
          awardKey="TEOTY"
          candidates={candidates.TEOTY}
          keyStats={['YRDS', 'TDS', 'REC', 'AVG']}
        />

        <AwardSection
          title="🛡️ SCBOTY – Slot Corner Back of the Year"
          awardKey="SCBOTY"
          candidates={candidates.SCBOTY}
          keyStats={['TCK', 'SCK', 'INT', 'PBU']}
        />

        <AwardSection
          title="🛡️ DCBOTY – Deep Corner Back of the Year"
          awardKey="DCBOTY"
          candidates={candidates.DCBOTY}
          keyStats={['TCK', 'SCK', 'INT', 'PBU']}
        />

        <AwardSection
          title="⚡ LBOTY – Linebacker of the Year"
          awardKey="LBOTY"
          candidates={candidates.LBOTY}
          keyStats={['TCK', 'TFL', 'SCK', 'FF']}
        />

        <AwardSection
          title="💥 DEOTY – Defensive End of the Year"
          awardKey="DEOTY"
          candidates={candidates.DEOTY}
          keyStats={['SCK', 'TCK', 'TFL', 'FF']}
        />

        <AwardSection
          title="💪 DTOTY – Defensive Tackle of the Year"
          awardKey="DTOTY"
          candidates={candidates.DTOTY}
          keyStats={['TCK', 'TFL', 'SCK', 'FF']}
        />

        <AwardSection
          title="👟 KOTY – Kicker of the Year"
          awardKey="KOTY"
          candidates={candidates.KOTY}
          keyStats={['PNTS', 'FG%', 'XP%', 'LONG']}
        />

        <AwardSection
          title="🛢️ OLOTY – Offensive Line of the Year"
          awardKey="OLOTY"
          candidates={candidates.OLOTY}
          keyStats={['SNPS', 'SKA', 'DP']}
        />

        <AwardSection
          title="🎖️ DPOY – Defensive Player of the Year"
          awardKey="DPOY"
          candidates={candidates.DPOY}
          keyStats={['TCK', 'SCK', 'INT', 'TD']}
        />

        <AwardSection
          title="🏆 MVP – Most Valuable Player"
          awardKey="MVP"
          candidates={candidates.MVP}
          keyStats={['POS', 'TEAM']}
        />
      </div>
    </div>
  );
}

export default AwardCandidates;
